import React, { useContext, useEffect, useState } from 'react'
import { itemRender } from '../../helper/paginationfunction'
import { Button, Dropdown } from 'react-bootstrap'
import ExportIcon from '../../../src/assets/img/svg/export-icon.svg'
import { DateRange } from 'react-date-range'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import TableView from '../../component/common/tableView'
import { SpinnerContext } from '../../context/spinnerContext'
import { useDispatch, useSelector } from 'react-redux'
import { getAllBilling } from '../../redux/slices/billingSlice'
import { convertTimestampToDate, getDayStartAndEndTimestamps, getTimeStamp } from '../../helper/function'
import { getAllPropertiesName } from '../../redux/slices/propertySlice'
import axios from 'axios'
import { getAuthToken } from '../../helper/storage'
import { errorToast, successToast } from '../../util/Toast/toast'
import { format } from 'date-fns'

const Billings = () => {
  const dispatch = useDispatch()
  const { changeLoader } = useContext(SpinnerContext)
  const { allBilling } = useSelector(({ billing }) => billing)
  const { allPropertiesName } = useSelector(({ property }) => property)
  const [selectedProperty, setSelectedProperty] = useState(null)

  const [pagesize, setPagesize] = useState(10)
  const [page, setPage] = useState(1)
  const [showDatePicker, setShowDatePicker] = useState(false)

  const [timeline, setTimeline] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection'
    }
  ])

  const [range, setRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: 'selection'
    }
  ])

  const clearSelection = () => {
    setRange([{ startDate: null, endDate: null, key: 'selection' }])
    setTimeline([{ startDate: new Date(), endDate: new Date(), key: 'selection' }])
    setShowDatePicker(false)
    setSelectedProperty(null)
  }

  const toggleDatePicker = () => {
    setShowDatePicker((prev) => !prev)
  }

  const handleSelect = (ranges) => {
    setRange([ranges.selection])
    setTimeline([ranges.selection])
    setPage(1)
    setShowDatePicker(false) // Close picker after selection (optional)
  }

  const handlePagination = async (page, pageSize) => {
    setPage(page)
    setPagesize(pageSize)
  }

  const handleSelectProperty = (propertyId) => {
    setShowDatePicker(false)
    setSelectedProperty(propertyId)
  }

  const handleInvoiceExport = async () => {
    try {
      // Extract and validate the start_date and end_date
      const start_date = getTimeStamp(range[0]?.startDate)
      const end_date = getTimeStamp(range[0]?.endDate)

      const { endOfDayTimestamp } = getDayStartAndEndTimestamps(end_date)

      // Construct the params object
      const params = {
        ...(selectedProperty && { properties_id: selectedProperty }),
        ...(isValidDate(start_date) && isValidDate(end_date) && { start_date, end_date: endOfDayTimestamp })
      }

      changeLoader(true)

      // Make the API call
      // eslint-disable-next-line no-undef
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/invoice/export-client-invoices`, {
        params,
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${getAuthToken()}` // Passing the token in the header
        }
      })

      // Check for successful response
      if (response.status === 200) {
        // Format the current date and time as 'DD MMM YYYY hh:mm A'
        const currentDateTime = format(new Date(), 'dd MMM yyyy hh:mm a')

        // Create a Blob URL for downloading the file
        const url = window.URL.createObjectURL(new Blob([response?.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `Invoice-${currentDateTime}.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.parentNode.removeChild(link)

        successToast(response?.data?.status_message || 'File downloaded successfully.')
      } else {
        throw new Error(response?.data?.status_message || 'An error occurred while exporting the invoice.')
      }
    } catch (err) {
      // Handle errors from the backend or network issues
      if (err.response && err.response.data) {
        errorToast(err.response.data.message || 'No invoices found.')
      } else {
        errorToast(err.message || 'Something went wrong. Please try again.')
      }
      console.error('>>Err:', err)
    } finally {
      changeLoader(false)
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      changeLoader(true)
      try {
        dispatch(getAllPropertiesName())
      } catch (err) {
        console.error('>>Err:', err)
      } finally {
        changeLoader(false)
      }
    }
    fetchData()
  }, [])

  const isValidDate = (date) => date && !isNaN(date)

  useEffect(() => {
    // Extract start and end dates
    const start_date = getTimeStamp(range[0]?.startDate)
    const end_date = getTimeStamp(range[0]?.endDate)

    // Get the end-of-day timestamp
    const { endOfDayTimestamp } = getDayStartAndEndTimestamps(end_date)

    // Construct the params object
    const params = {
      page,
      limit: pagesize,
      ...(selectedProperty && { properties_id: selectedProperty }),
      ...(isValidDate(start_date) && isValidDate(end_date) && { start_date: start_date, end_date: endOfDayTimestamp })
    }
    changeLoader(true)
    dispatch(getAllBilling(params)).finally(() => {
      changeLoader(false)
    })
  }, [page, pagesize, range, selectedProperty])

  const columns = [
    {
      title: 'ID',
      dataIndex: 'invoice_no',
      render: (text) => <p>#{text}</p>
    },

    {
      title: 'Date',
      dataIndex: 'created_at',
      render: (text) => <p>{convertTimestampToDate(text)}</p>
    },
    {
      title: 'Property',
      dataIndex: 'property',
      render: (_, record) => <p>{record?.propertySchedule?.properties?.property_name}</p>
    },
    {
      title: 'Cleaner',
      dataIndex: 'cleaner',
      render: (_, record) =>
        record?.propertySchedule?.scheduleCleaners?.cleaners?.first_name ? (
          <p>
            {record?.propertySchedule?.scheduleCleaners?.cleaners?.first_name || ''}
            {record?.propertySchedule?.scheduleCleaners?.cleaners?.last_name || ''}
          </p>
        ) : (
          <p>-</p>
        )
    },
    {
      title: 'Payment Status',
      dataIndex: 'status',
      render: (_, record) => <p style={{ textTransform: 'capitalize' }}>{record?.status}</p>
    },
    {
      title: 'Coupon',
      dataIndex: 'coupon',
      render: (_, record) => <p>{record?.coupon ? record?.coupon?.code : '-'}</p>
    },
    {
      title: 'Payment',
      dataIndex: 'payment',
      render: (_, record) => (
        <p>
          {record?.final_amount} {record?.unit}
        </p>
      )
    }
  ].filter(Boolean)

  const paginationConfig = {
    total: allBilling?.totalRecords || 0,
    defaultPageSize: allBilling?.limit || 10,
    defaultCurrent: allBilling?.currentpage || 1,
    current: page,
    pageSize: pagesize,
    showSizeChanger: true,
    pageSizeOptions: [10, 25, 50, 100],
    onChange: (page, pageSize) => handlePagination(page, pageSize),
    showTotal: (total, range) => `Showing ${range[0]} to ${range[1]} of ${total} Entries`,
    itemRender: itemRender
  }

  return (
    <div className="page-wrapper customers">
      <div className="">
        <div className="page-header">
          <div className="heading_header_div">
            <h4>Billing History</h4>
            <div className="list-btn d-flex gap-2 mt-0 position-relative flex-wrap">
              <button type="button" className="btn btn-danger" onClick={clearSelection}>
                Clear Selection
              </button>

              <Button className="btn btn-primary" onClick={toggleDatePicker}>
                Date Range
              </Button>
              {showDatePicker && (
                <div className="date_picker_box_div">
                  <DateRange
                    editableDateInputs
                    onChange={(ranges) => handleSelect(ranges)}
                    moveRangeOnFirstSelection={false}
                    showSelectionPreview={true}
                    ranges={timeline}
                    direction="vertical"
                    style={{ zIndex: 1000 }}
                  />
                </div>
              )}
              <div>
                <Dropdown className="dropdown_btn_div">
                  <Dropdown.Toggle variant="success" id="dropdown-basic">
                    Property
                    {/* {selectedProperty ? allPropertiesName?.find((prop) => prop.properties_id === selectedProperty).property_name : 'Property'} */}
                  </Dropdown.Toggle>

                  <Dropdown.Menu className="dropdown_btn_menu">
                    <Dropdown.Item onClick={() => handleSelectProperty(null)}>All Properties</Dropdown.Item>
                    {allPropertiesName?.map((property) => (
                      <Dropdown.Item key={property.properties_id} onClick={() => handleSelectProperty(property.properties_id)}>
                        <div className="dropdown_btn_menu_item_div">{property.property_name}</div>
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
              {/* <Button className="btn btn-primary">Property</Button> */}
              <a className="fs-14 black_color fw-500 d-flex align-items-center gap-1 text-decoration-underline" onClick={() => handleInvoiceExport()}>
                <img className="img-fluid" src={ExportIcon} alt="Download Icon" />
                Export
              </a>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-12">
            <div className="table-card-div">
              <div className="card-table">
                <div className="card-body purchase">
                  <div className="table-responsive table-hover">
                    <TableView
                      columns={columns}
                      dataSource={allBilling?.invoices || []}
                      paginationConfig={paginationConfig}
                      scroll={allBilling?.invoices?.length > 0 ? { x: true } : false}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Billings
